import React, { FunctionComponent, useEffect, useState } from "react";
import { Box } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { Table, TableMessage } from "components/UI";
import { SystemEmailTemplate } from "model/SystemEmailTemplate";
import {
  fetchListThunk,
  fetchTemplateThunk,
  reset as resetSystemEmailTemplates
} from "actions/systemEmailTemplates/SystemEmailTemplatesActions";
import EmailTemplatesHeader from "./UI/EmailTemplatesHeader";
import TableHeader from "./UI/Table/TableHeader";
import TableBody from "./UI/Table/TableBody";
import TemplatePreviewModal from "./UI/TemplatePreviewModal";
import EmailTemplateEditor from "./UI/EmailTemplateEditor";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%"
    },
    headerContainer: {
      [theme.breakpoints.down("sm")]: { paddingBottom: "8px" },
      [theme.breakpoints.only("md")]: { paddingBottom: "12px" },
      [theme.breakpoints.only("lg")]: { paddingBottom: "17px" },
      [theme.breakpoints.up("xl")]:  { paddingBottom: "25px" }
    },
    table: {
      [theme.breakpoints.down("sm")]: { marginTop: "15px" },
      [theme.breakpoints.only("md")]: { marginTop: "25px" },
      [theme.breakpoints.only("lg")]: { marginTop: "33px" },
      [theme.breakpoints.up("xl")]:  { marginTop: "50px" }
    }
  })
);

type Mode = "list" | "editor";

/**
 * Root of the Email Templates feature. Single-route + state-toggle pattern —
 * matches the Superadmins convention in this app rather than the marketplace's
 * multi-route editor flow.
 *
 *   Mode "list"   → table + Create Template button. Row click opens preview.
 *   Mode "editor" → full-viewport editor (Unlayer). Reached by clicking
 *                   Create Template OR by clicking Edit inside the preview.
 *
 * Preview is overlaid on top of the list (MUI Dialog fullScreen) so closing
 * it drops the user back to the list with no state churn.
 */
export const EmailTemplates: FunctionComponent = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const list = useSelector<RootState, Array<SystemEmailTemplate>>(s => s.systemEmailTemplates.list);
  const loadingList = useSelector<RootState, boolean>(s => s.systemEmailTemplates.loadingList);
  const currentTemplate = useSelector<RootState, SystemEmailTemplate | undefined>(
    s => s.systemEmailTemplates.currentTemplate
  );

  const [mode, setMode] = useState<Mode>("list");
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | undefined>(undefined);

  useEffect(() => {
    dispatch(fetchListThunk());
    return () => {
      dispatch(resetSystemEmailTemplates());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------

  /**
   * Row click → load the full template (html + design_json) and open preview.
   * The full-fetch happens here, not lazily in the modal, so the modal stays
   * dumb about the data layer.
   */
  const handleSelectTemplate = (template: SystemEmailTemplate) => {
    dispatch(fetchTemplateThunk(template.id));
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  /**
   * Edit from preview header → close preview, open editor with the loaded
   * template id (the slice already has `currentTemplate` populated).
   */
  const handleEditFromPreview = () => {
    if (!currentTemplate) return;
    setEditingTemplateId(currentTemplate.id);
    setPreviewOpen(false);
    setMode("editor");
  };

  /** Create Template button → open editor with no template id. */
  const handleCreate = () => {
    setEditingTemplateId(undefined);
    setMode("editor");
  };

  /**
   * Save & Exit OR Cancel inside the editor — both return to the list.
   * The reducer already spliced the saved row into the list, so no refetch.
   */
  const handleExitEditor = () => {
    setEditingTemplateId(undefined);
    setMode("list");
  };

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------

  if (mode === "editor") {
    return (
      <EmailTemplateEditor
        templateId={editingTemplateId}
        onExit={handleExitEditor}
      />
    );
  }

  const isEmpty = !loadingList && list.length === 0;

  return (
    <Box className={classes.container}>
      <OverlayScrollbarsComponent style={{ height: "100%", width: "100%" }}>
        <Box className={classes.headerContainer}>
          <EmailTemplatesHeader onCreate={handleCreate} />
        </Box>

        {isEmpty ? (
          <TableMessage
            title="No templates yet"
            description="Create your first system email template using the button above."
          />
        ) : (
          <Table
            head={<TableHeader />}
            body={<TableBody templates={list} onSelect={handleSelectTemplate} />}
            loading={loadingList}
            className={classes.table}
          />
        )}

        <TemplatePreviewModal
          open={previewOpen}
          onClose={handleClosePreview}
          onEdit={handleEditFromPreview}
        />
      </OverlayScrollbarsComponent>
    </Box>
  );
};

export default EmailTemplates;
